import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { QRCodeCanvas } from 'qrcode.react';
import { BsWhatsapp, BsClipboardCheck } from 'react-icons/bs';
import { FaDownload } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { shareViaWhatsApp } from '../utils/shareUtils';
import { toInlineAssetUrl } from '../utils/receiptImage';

const QR_CANVAS_ID = 'qr-catalog-share';

/**
 * CatalogShareModal - QR + share options for a business's public catalog link.
 * @param {boolean} show
 * @param {function} onHide
 * @param {object} business - { slug, logo_url, name }
 */
const CatalogShareModal = ({ show, onHide, business }) => {
    const { t } = useTranslation();
    const [logoStatus, setLogoStatus] = useState('idle'); // idle | loading | ready | error

    const catalogUrl = business?.slug
        ? `${window.location.origin}/business/${business.slug}`
        : null;

    const inlineLogoUrl = useMemo(
        () => (business?.logo_url ? toInlineAssetUrl(business.logo_url) : null),
        [business?.logo_url]
    );

    // QRCodeCanvas has no onImageLoad callback, so we preload the logo ourselves
    // to know when it's safe to enable the download button (and to degrade to a
    // plain QR, instead of a tainted canvas, if the logo fails to load).
    useEffect(() => {
        if (!inlineLogoUrl) {
            setLogoStatus('idle');
            return;
        }
        setLogoStatus('loading');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setLogoStatus('ready');
        img.onerror = () => setLogoStatus('error');
        img.src = inlineLogoUrl;
    }, [inlineLogoUrl]);

    if (!catalogUrl) return null;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(catalogUrl)
            .then(() => toast.success(t('toast.linkCopied')))
            .catch(() => toast.error(t('toast.copyFailed')));
    };

    const handleWhatsAppClick = () => {
        shareViaWhatsApp(t('catalogShareModal.whatsappMessage', {
            businessName: business?.name,
            link: catalogUrl
        }));
    };

    const handleDownloadClick = () => {
        try {
            const canvas = document.getElementById(QR_CANVAS_ID);
            const url = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = url;
            a.download = `catalogo-${business.slug}.png`;
            a.click();
        } catch (error) {
            console.error('QR download failed', error);
            toast.error(t('catalogShareModal.downloadError'));
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('catalogShareModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                <p className="text-muted small mb-3">{t('catalogShareModal.hint')}</p>

                <QRCodeCanvas
                    id={QR_CANVAS_ID}
                    value={catalogUrl}
                    size={220}
                    level="H"
                    includeMargin
                    imageSettings={
                        logoStatus === 'ready'
                            ? { src: inlineLogoUrl, height: 44, width: 44, excavate: true, crossOrigin: 'anonymous' }
                            : undefined
                    }
                />

                <div className="mt-3">
                    <a
                        href={catalogUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted text-break small"
                    >
                        {catalogUrl}
                    </a>
                </div>

                <div className="d-flex justify-content-center flex-wrap gap-2 mt-3">
                    <Button
                        variant="light"
                        onClick={handleCopyLink}
                        className="d-flex align-items-center gap-2"
                    >
                        <BsClipboardCheck size={20} /> <span>{t('catalogShareModal.copyLink')}</span>
                    </Button>

                    <Button
                        variant="light"
                        onClick={handleWhatsAppClick}
                        className="d-flex align-items-center gap-2"
                        aria-label="WhatsApp"
                    >
                        <BsWhatsapp size={20} /> <span>{t('catalogShareModal.whatsapp')}</span>
                    </Button>

                    <Button
                        variant="light"
                        onClick={handleDownloadClick}
                        disabled={logoStatus === 'loading'}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaDownload size={20} /> <span>{t('catalogShareModal.downloadQR')}</span>
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default CatalogShareModal;
